import moment from "moment";

import { DateTime } from "@/Common/Helpers/DateTime";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Guid } from "@/Common/Models/Guid";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { MedicalItemsTab, PatientTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { EncounterSearchIndexRebuildMode, PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemProblemAndCondition } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { MedicalItemScreeningTest } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { FollowUpResponse } from "@/Patient/Models/MedicalSummary/Response/FollowUpResponse";
import { MedicalItemScreeningTestResponse } from "@/Patient/Models/MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { MedicalItemViewMode } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientMedicalItemsHandler extends BaseOperationHandler {
    private helper: MyleHelper = new MyleHelper();

    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.MedicalSummary.Details_BackToSummary, this.backToSummary);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.LoadEMRTemplateComments, this.loadComments);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.SearchTermChanged, this.searchTermChanged);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.SelectEMRTemplate, this.selectEMRTemplateId);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.ToggleVisibility, this.toggleVisibility);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.ToggleItemCollapse, this.toggleCollapse);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.ToggleAddComment, this.toggleAddComment);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.CancelAddComment, this.toggleAddComment);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.SaveAddComment, this.saveAddComment);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.ToggleInvalid, this.toggleInvalid);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.SaveValidationNote, this.saveAddValidationNote);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.SaveValidationNoteForImmunization, this.saveAddValidationNoteForImmunization);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.RemoveMedicalItem, this.showRemoveMedicalItemPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.CancelRemoveMedicalItem, this.cancelRemoveMedicalItem);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.ConfirmRemoveMedicalItem, this.confirmRemoveMedicalItem);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.ReturnToNote, this.returnToNote);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.ReturnToSummary, this.returnToSummary);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.ReturnToPatientResults, this.returnToPatientResults);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.Modify, this.modify);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.Save, this.save);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.Cancel, this.cancel);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.NewEntry, this.handleNewEntry);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.SetExistingScreeningItemForNewEntry, this.setExistingScreeningItemForNewEntry);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.UpdateFollowupStatus, this.handleFollowupStatusChanged);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.InsertMedicalRequestComment, this.saveMedicalRequestComment);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.UpdateScreeningsModelAfterPrintingRequest, this.updateScreeningsModelAfterPrintingRequest);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.TogglePharmaNetLoginComponent, this.togglePharmaNetLoginComponent);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.ToggleAsFavorite, this.toggleAsFavorite);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.SaveRiskFactors, this.saveRiskFactors);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.SaveImmunizations, this.saveImmunizations);
        this.RegisterOperation(LocalPatientOperations.MedicalItems.ToggleFavoriteMedicalItem, this.toggleFavoriteMedicalItem);
    }

    private returnToSummary(request: LocalEvent, store: PatientStore): void {
        let activePatientID = request.PatientId;
        let patientUI = store.PatientUIModels[activePatientID];

        patientUI = patientUI.set("ActiveVerticalTab", PatientTab.MedicalSummary);
        patientUI = patientUI.set("PatientMedicalItemsActiveTab", MedicalItemsTab.NONE);
        store.PatientUIModels[activePatientID] = patientUI;
    }

    private returnToPatientResults(request: LocalEvent, store: PatientStore): void {
        const activePatientID = request.PatientId;
        let patientUI = store.PatientUIModels[activePatientID];

        patientUI = patientUI.set("ActiveVerticalTab", PatientTab.Results);
        patientUI = patientUI.set("PatientMedicalItemsActiveTab", MedicalItemsTab.NONE);
        store.PatientUIModels[activePatientID] = patientUI;
    }

    private returnToNote(request: LocalEvent, store: PatientStore): void {
        let activePatientID = request.PatientId;

        // if we are moving back from a "new medical item" creation, make sure to remove all medical items with IsNew=true
        let summaryModel = store.PatientMedicalSummaryModels[activePatientID];
        summaryModel = summaryModel.set("MedicalItemsProblemsOnGoing", summaryModel.get("MedicalItemsProblemsOnGoing").toArray().filter(x => !x.IsNewMedicalItem).toImmutable());
        store.PatientMedicalSummaryModels[activePatientID] = summaryModel;

        // No Medicalnote, return to summary;
        if (store.PatientMedicalNoteModels[activePatientID] == null || patientStore.IsMedicalItemFromOutsideNote) {
            this.returnToSummary(request, store);
        }
        else {
            let patientUI = store.PatientUIModels[activePatientID];
            patientUI = patientUI.set("ActiveVerticalTab", PatientTab.MedicalNote);
            patientUI = patientUI.set("PatientMedicalItemsActiveTab", MedicalItemsTab.NONE);
            store.PatientUIModels[activePatientID] = patientUI;
        }
    }

    private backToSummary(request: LocalEvent, store: PatientStore): void {
        let patientUI = store.PatientUIModels[request.PatientId];
        patientUI = patientUI.set("PatientMedicalItemsActiveTab", MedicalItemsTab.NONE);
        store.PatientUIModels[request.PatientId] = patientUI;
    }

    private loadComments(request: LocalEvent, store: PatientStore): void {
        let emrtTemplateId = request.Get<string>("value");
        store.ActionsMedicalSummary.LoadEMRTemplateComments(emrtTemplateId);
    }

    private searchTermChanged(request: LocalEvent, store: PatientStore): void {
        let value = request.Get<string>("value");
        let searchModel = store.PatientMedicalItemsModels[request.PatientId];
        searchModel = searchModel.set("SearchTerm", value);

        store.PatientMedicalItemsModels[request.PatientId] = searchModel;
    }

    private selectEMRTemplateId(request: LocalEvent, store: PatientStore): void {
        const value = request.Get<string>("value");
        const cancelEdit = request.Get<string>("cancelEdit");

        if (cancelEdit)
            this.cancel(request, store);

        let detailsModel = store.PatientMedicalItemsModels[request.PatientId];
        let summaryModel = store.PatientMedicalSummaryModels[request.PatientId];
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];

        detailsModel = detailsModel.set("SelectedEMRTemplateId", value);
        detailsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(detailsModel, null, detailsModel.get("NavigationOrigin"), summaryModel, (noteModel != null) ? noteModel.get("Encounter").Id : null, store.ActionsMedicalSummary.LoadEMRTemplateComments);
        store.PatientMedicalItemsModels[request.PatientId] = detailsModel;
    }

    private toggleVisibility(request: LocalEvent, store: PatientStore): void {
        const emrTemplateId = request.Get<string>("value");
        const isDisplayedInCPP = request.Get<boolean>("isDisplayedInCPP");
        let itemsModel = store.PatientMedicalItemsModels[request.PatientId];

        if (itemsModel.get("ViewMode") == MedicalItemViewMode.Edit) {
            const medicalItems = itemsModel.get("MedicalItemsList").toArray();
            const isAnyUncomitted = medicalItems.some(x => !x.IsCommitted);
            if (isAnyUncomitted) {
                medicalItems.forEach(x => {
                    x.EMRTemplateIsVisibleInCPP = !x.EMRTemplateIsVisibleInCPP;
                    x.HideDate = this.helper.CreateDateAsUTC(moment().toDate());
                });
                store.PatientMedicalItemsModels[request.PatientId] = itemsModel.set("MedicalItemsList", Immutable.List(medicalItems));
                return;
            }
        }

        store.ActionsMedicalSummary.ToggleVisibility(emrTemplateId, isDisplayedInCPP);
    }

    private toggleCollapse(request: LocalEvent, store: PatientStore): void {
        let itemId = request.Get<string>("value");
        let detailsModel = store.PatientMedicalItemsModels[request.PatientId];
        let displayedItems = detailsModel.get("MedicalItemsList").toArray();

        displayedItems = displayedItems.map(x => {
            if (x.MedicalItemId == itemId || x.displayId == itemId)
                x.IsCollapsed = !x.IsCollapsed;

            return x;
        });

        detailsModel = detailsModel.set("MedicalItemsList", Immutable.List(displayedItems));
        store.PatientMedicalItemsModels[request.PatientId] = detailsModel;
    }

    private toggleAddComment(request: LocalEvent, store: PatientStore): void {
        let detailsModel = store.PatientMedicalItemsModels[request.PatientId];
        let showComment = !detailsModel.get("IsInCommentEdit");
        detailsModel = detailsModel.set("IsInCommentEdit", showComment);

        if (!showComment)
            detailsModel = detailsModel.set("CommentText", "");

        store.PatientMedicalItemsModels[request.PatientId] = detailsModel;
    }

    private saveAddComment(request: LocalEvent, store: PatientStore): void {
        let detailsModel = store.PatientMedicalItemsModels[request.PatientId];
        let comment = request.Get<string>("value");
        let employeeId = sessionStore.getCurrentEmployee().ContactFacetId;
        let emrId = detailsModel.get("SelectedEMRTemplateId");
        detailsModel = detailsModel.set("IsInCommentEdit", false);
        store.PatientMedicalItemsModels[request.PatientId] = detailsModel;
        store.ActionsMedicalSummary.SaveEMRTemplateComment(emrId, employeeId, comment);
    }

    private toggleInvalid(request: LocalEvent, store: PatientStore): void {
        let detailsModel = store.PatientMedicalItemsModels[request.PatientId];
        let summaryModel = store.PatientMedicalSummaryModels[request.PatientId];
        let selectedEMRId = detailsModel.get("SelectedEMRTemplateId");
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];

        if (selectedEMRId) {
            let allItems = detailsModel.get("AllMedicalItemsList").toArray();

            detailsModel = detailsModel.set("AllMedicalItemsList", Immutable.List(allItems));
            detailsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(detailsModel, null, detailsModel.get("NavigationOrigin"), summaryModel, (noteModel != null) ? noteModel.get("Encounter").Id : null, store.ActionsMedicalSummary.LoadEMRTemplateComments);
            detailsModel = detailsModel.set("IsInValidationEdit", !detailsModel.get("IsInValidationEdit"));
            detailsModel = detailsModel.set("ValidationNoteText", "");
        }

        store.PatientMedicalItemsModels[request.PatientId] = detailsModel;
    }

    private prepareAddValidationNote(request: LocalEvent, store: PatientStore): { note: string, emrTemplateId: string, employeeId: string } {
        let detailsModel = store.PatientMedicalItemsModels[request.PatientId];
        detailsModel = detailsModel
            .set("ValidationNoteText", "")
            .set("IsInValidationEdit", false);

        let note = request.Get<string>("value");
        let emrTemplateId = detailsModel.get("SelectedEMRTemplateId");
        let employeeId = sessionStore.getCurrentEmployee().ContactFacetId;

        store.PatientMedicalItemsModels[request.PatientId] = detailsModel;

        return { note, emrTemplateId, employeeId };
    }

    private saveAddValidationNote(request: LocalEvent, store: PatientStore): void {
        const { note, emrTemplateId, employeeId } = this.prepareAddValidationNote(request, store);
        store.ActionsMedicalSummary.ToggleValidity(emrTemplateId, employeeId, note);
    }

    private saveAddValidationNoteForImmunization(request: LocalEvent, store: PatientStore): void {
        const { note, emrTemplateId, employeeId } = this.prepareAddValidationNote(request, store);

        const medicalItemId = request.Get<string>("medicalItemId");

        store.ActionsMedicalSummary.ToggleValidityForImmunization(emrTemplateId, employeeId, note, medicalItemId);
    }

    private showRemoveMedicalItemPopup(request: LocalEvent, store: PatientStore): void {
        let medicalItem = request.Get<MedicalItem>("medicalItem");

        let detailsModel = store.PatientMedicalItemsModels[request.PatientId];

        detailsModel = detailsModel.set("IsInValidationEdit", true);
        detailsModel = detailsModel.set("MedicalItemToDelete", medicalItem);
        detailsModel = detailsModel.set("IsInValidationEdit", false);
        detailsModel = detailsModel.set("ShowDeleteMedicalItemConfirmation", true);

        store.PatientMedicalItemsModels[request.PatientId] = detailsModel;
        sessionStore.showGlobalOverlay(true);
    }

    private cancelRemoveMedicalItem(request: LocalEvent, store: PatientStore): void {
        let detailsModel = store.PatientMedicalItemsModels[request.PatientId];
        detailsModel = detailsModel.set("ShowDeleteMedicalItemConfirmation", false);
        detailsModel = detailsModel.set("MedicalItemToDelete", null);

        store.PatientMedicalItemsModels[request.PatientId] = detailsModel;
        sessionStore.hideGlobalOverlay();
    }

    private confirmRemoveMedicalItem(request: LocalEvent, store: PatientStore): void {
        let detailsModel = store.PatientMedicalItemsModels[request.PatientId];
        let medicalItem = detailsModel.get("MedicalItemToDelete");

        if (!medicalItem || medicalItem.IsCommitted)
            return;

        let medicalItemId = medicalItem.MedicalItemId;
        let medicalItemType = medicalItem.EMRTemplateMedicalItemType;
        store.ActionsMedicalItems.deleteMedicalItem(medicalItemId, medicalItemType, request.PatientId);
    }

    // if Item is committed and the related note is the active note, create a copy of this item.
    // if Item is uncommited and the related note is NOT the active note, create a copy of this item, and signOff all the other that are currently uncommited since we are gonna make a new contribution.
    // if Item is uncommited, modify it (nothing special is done here)
    private modify(request: LocalEvent, store: PatientStore): void {
        let itemsModel = store.PatientMedicalItemsModels[request.PatientId];
        itemsModel = itemsModel.set("ViewMode", MedicalItemViewMode.Edit);

        const notesModel = store.PatientMedicalNoteModels[request.PatientId];
        // get the active encounter ID, so we can set the medicalItem's relatedEncounterId
        const activeEncounterId = notesModel ? notesModel.get("Encounter").Id : "";
        const selectedEMRTemplateId = itemsModel.get("SelectedEMRTemplateId");

        // Collapse all items;
        const medicalItems = itemsModel.get("MedicalItemsList").toArray();
        medicalItems.forEach(x => x.IsCollapsed = true);

        if (medicalItems?.any()) {
            const nonCommittedMedicalItems = medicalItems.filter(item => !item.IsCommitted);

            // Get latest non-committed medical item.
            // When all medical items are committed, take the latest.
            let latestMedicalItem: MedicalItem;
            if (nonCommittedMedicalItems.length > 0) {
                latestMedicalItem = MedicalItemModel.sortByCreationDateDesc(nonCommittedMedicalItems).firstOrDefault();

                // SignOff the others
                nonCommittedMedicalItems.forEach(mi => {
                    if (mi.Id != latestMedicalItem.Id) {
                        mi.CommitDate = DateTime.nowMoment().toDate();
                        mi.IsCommitted = true;
                    }
                });
            }
            else
                latestMedicalItem = MedicalItemModel.sortByCreationDateDesc(medicalItems).firstOrDefault();

            if (latestMedicalItem == null)
                throw "Invalid EMRTemplate - Modification of an Empty EMRTemplate?"; // Modification of an Empty EMRTemplate?

            const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
            const encounter = patientNoteModel?.get("Encounter");

            if (latestMedicalItem.IsCommitted || latestMedicalItem.RelatedEncounterId != activeEncounterId) {
                // Create a copy

                // SignOff the current non committed since we are gonna create a new one
                nonCommittedMedicalItems.forEach(mi => {
                    if (mi.Id == latestMedicalItem.Id) {
                        mi.CommitDate = DateTime.nowMoment().toDate();
                        mi.IsCommitted = true;
                    }
                });

                medicalItems.push(this.createNewMedicalItemEntry(latestMedicalItem, selectedEMRTemplateId, encounter?.Id));
            }
            else {
                // Just modify the current uncommitted item
                medicalItems.filter(x => x.MedicalItemId == latestMedicalItem.MedicalItemId).forEach(x => x.IsCollapsed = false);
            }
        }

        itemsModel = itemsModel.set("MedicalItemsList", Immutable.List(medicalItems));
        store.PatientMedicalItemsModels[request.PatientId] = itemsModel;
    }

    private save(request: LocalEvent, store: PatientStore): void {
        const isSavePlus = request.Get<boolean>("isSavePlus") ?? false;
        const itemsToBeSaved = request.Get<MedicalItemProblemAndCondition[]>("editedItems");
        const view = request.Get<MedicalItemsTab>("view");
        const followupsToLink = request.Get<FollowUpResponse[]>("followupsToLink");
        const followupsToUnlink = request.Get<FollowUpResponse[]>("followupsToUnlink");

        if (itemsToBeSaved.length == 0 && view != MedicalItemsTab.Medication)
            return; // There is nothing to save....

        const patientModel = store.PatientModels[request.PatientId];
        let itemsModel = store.PatientMedicalItemsModels[request.PatientId];
        const notesModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = notesModel?.get("Encounter");
        const patientContactId = request.PatientId;
        const defaultSpecialization = sessionStore.getCurrentEmployeeDefaultSpecializationLookupId();

        if (!defaultSpecialization) {
            store.PatientMedicalSummaryModels[patientContactId] = store.PatientMedicalSummaryModels[patientContactId]
                .set("ShowNoteCreationSpecialityErrorWarning", true);
            sessionStore.showGlobalOverlay(true);
            return;
        }

        const latestMedicalItemIdInTheEMRTemplateTheUserCurrentlySees = PatientMedicalItemsHelper.getLatestMedicalItemIdForEMRTemplate(itemsModel.get("MedicalItemsList").toArray());

        itemsModel = itemsModel
            .set("IsSaving", true)
            .set("IsSavePlusActivated", isSavePlus)
            .set("ItemsToBeSaved", Immutable.List(itemsToBeSaved))
            .set("ItemView", view);

        // special case if we are saving an activeMedication
        if (view === MedicalItemsTab.Medication) {
            const baseMedicationModel: MedicationModel = request.Get<MedicationModel>("baseMedicationItem");
            const newMedicationModel: MedicationModel = request.Get<MedicationModel>("newMedicationModel");

            itemsModel = itemsModel
                .set("ActiveMedicationEditBaseMedicalItem", baseMedicationModel)
                .set("ActiveMedicationEditNewMedicalItem", newMedicationModel);
        }

        store.PatientMedicalItemsModels[request.PatientId] = itemsModel;

        // Call checkStatus to validate note isnt signed yet
        if (encounter) {
            store.ActionsMedicalNote.GetEncounterStatus(
                encounter.Id,
                "saveMedicalItem",
                request.PatientId,
                () => { // If note is not Approuved...
                    store.ActionsMedicalItems.SaveMedicalItem(
                        itemsModel,
                        latestMedicalItemIdInTheEMRTemplateTheUserCurrentlySees,
                        patientModel,
                        notesModel,
                        patientContactId,
                        false,
                        followupsToLink,
                        followupsToUnlink
                    );

                    let summaryModel = store.PatientMedicalSummaryModels[request.PatientId];

                    summaryModel = PatientMedicalSummaryHelper.makeEncountersSearchStringsReset(summaryModel);
                    if (patientStore.IsMedicalItemFromOutsideNote) {
                        summaryModel = PatientMedicalSummaryHelper.FilterEncountersClientSide(summaryModel, EncounterSearchIndexRebuildMode.SkipRebuild);
                    }

                    store.PatientMedicalSummaryModels[request.PatientId] = summaryModel;
                }
            );
        }
        store.deleteMedicalItemCachedState(request.PatientId, store.getCurrentlyEditedMedicalItemId());
    }

    private cancel(request: LocalEvent, store: PatientStore): void {
        const backToNote = request.Get<string>("item") == "backtonote";
        let itemsModel = store.PatientMedicalItemsModels[request.PatientId];
        itemsModel = itemsModel.set("ViewMode", MedicalItemViewMode.View);
        let medicalItems = itemsModel.get("MedicalItemsList").toArray();

        if (medicalItems.some(x => x.IsNewMedicalItem)) {
            medicalItems = medicalItems.filter(x => !x.IsNewMedicalItem); // make sure to remove any "isNew" medical item -> these would be unsaved medical items that we'll just discard
            let latestMedicalItem = medicalItems
                .sortByPropertyDesc(x => moment(x.CreationDate).toDate())
                .firstOrDefault();

            latestMedicalItem && medicalItems.forEach(x => {
                if (x.Id === latestMedicalItem.Id) {
                    x.IsCommitted = false;
                    x.CommitDate = null;
                }
            });
        }

        itemsModel = itemsModel.set("MedicalItemsList", Immutable.List(medicalItems));
        store.PatientMedicalItemsModels[request.PatientId] = itemsModel;

        if (backToNote)
            this.returnToNote(request, store);

        store.deleteMedicalItemCachedState(request.PatientId, store.getCurrentlyEditedMedicalItemId());
    }

    /**
     * Normally we would extract the New Entry logic out of the Modify logic and call the appropriate method directly from the BaseMedicalItemView.
     * Unfortunately, that would mean that ScreeningItems will affect all the Medical Item, and even more the killswitch would not be able to separate
     * this logic.
     * We will leave it as is for now so all the other medical items are intact and we will raise a new Task to clean this up after ScreeningItems are
     * stable in Prod.
     *
     * @param request
     * @param store
     */
    private handleNewEntry(request: LocalEvent, store: PatientStore): void {
        let itemsModel = store.PatientMedicalItemsModels[request.PatientId];
        itemsModel = itemsModel.set("ViewMode", MedicalItemViewMode.Edit);

        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = patientNoteModel?.get("Encounter");

        const medicalItems = itemsModel.get("MedicalItemsList").toArray();

        if (medicalItems?.length > 0 && encounter) {
            const latestMedicalItem = medicalItems.sortByPropertyDesc(x => moment(x.CreationDate).toDate()).firstOrDefault();

            if (latestMedicalItem && latestMedicalItem.RelatedEncounterId === encounter.Id) {
                medicalItems.forEach(x => {
                    x.IsCollapsed = true;
                    if (!x.IsCommitted) {
                        x.CommitDate = DateTime.nowMoment().toDate();
                        x.IsCommitted = true;
                    }
                });

                medicalItems.push(this.createNewMedicalItemEntry(latestMedicalItem, itemsModel.get("SelectedEMRTemplateId"), encounter.Id));

                itemsModel = itemsModel.set("MedicalItemsList", Immutable.List(medicalItems));
            }
        }

        store.PatientMedicalItemsModels[request.PatientId] = itemsModel;
    }

    private setExistingScreeningItemForNewEntry(request: LocalEvent, store: PatientStore): void {
        let itemsModel = store.PatientMedicalItemsModels[request.PatientId];
        let patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];

        let selectedScreeningItem = request.Get<MedicalItemScreeningTestResponse>("selectedScreeningItem");
        let EMRTemplateId = selectedScreeningItem.EMRTemplateId;
        const encounter = patientNoteModel?.get("Encounter");

        let screeningItems = PatientMedicalSummaryHelper.GetMedicalItemsToDisplay(itemsModel.get("AllMedicalItemsList"), EMRTemplateId);
        let latestScreeningItem = screeningItems.sortByPropertyDesc(x => moment(x.CreationDate).toDate()).firstOrDefault();
        const hasBeenModifiedInNewerEncounter = latestScreeningItem.ConsultationDate > encounter?.ConsultationDate;

        itemsModel = itemsModel.set("MedicalItemsList", Immutable.List(screeningItems));
        itemsModel = itemsModel.set("SelectedEMRTemplateId", EMRTemplateId);
        store.PatientMedicalItemsModels[request.PatientId] = itemsModel;

        if (encounter?.Id == latestScreeningItem.RelatedEncounterId) {
            this.handleNewEntry(request, store);
        }
        else {
            if (hasBeenModifiedInNewerEncounter) {
                request.AddItem("value", latestScreeningItem.EMRTemplateId);
                request.AddItem("cancelEdit", true);
                this.selectEMRTemplateId(request, store);
            }
            else {
                this.modify(request, store);
            }
        }
    }

    private handleFollowupStatusChanged(request: LocalEvent, store: PatientStore): void {
        let medicalRequestId = request.Get<string>("medicalRequestId");
        let medicalRequestType = request.Get<MedicalRequestType>("medicalRequestType");
        let statusLookupId = request.Get<string>("statusLookupId");

        store.ActionsMedicalItems.UpdateFollowupStatus(medicalRequestId, medicalRequestType, statusLookupId);
    }

    private saveMedicalRequestComment(request: LocalEvent, store: PatientStore): void {
        let requestId = request.Get<string>("requestId");
        let medicalrequestype = request.Get<MedicalRequestType>("requestType");
        let comment = request.Get<string>("comment");

        let itemModel = store.PatientMedicalItemsModels[request.PatientId];
        let notesModel = store.PatientMedicalNoteModels[request.PatientId];
        let summaryModel = store.PatientMedicalSummaryModels[request.PatientId];

        if (itemModel)
            itemModel = itemModel.set("AllMedicalItemsList", this.updateFollowUpComment(itemModel.get("AllMedicalItemsList"), requestId, medicalrequestype, comment));

        if (notesModel)
            notesModel = notesModel.set("MedicalItemsScreeningTest", this.updateFollowUpComment(notesModel.get("MedicalItemsScreeningTest"), requestId, medicalrequestype, comment));

        if (summaryModel)
            summaryModel = summaryModel
                .set("MedicalItemsScreeningTest", this.updateFollowUpComment(summaryModel.get("MedicalItemsScreeningTest"), requestId, medicalrequestype, comment))
                .set("FollowUps", this.updateFollowUpComposite(summaryModel.get("FollowUps"), requestId, medicalrequestype, comment));
    }

    private createNewMedicalItemEntry(medicalItem: MedicalItem, emrTemplateId: string, encounterId: Guid): MedicalItem {
        let newEntry = PatientMedicalItemsHelper.getMedicalItemCopy(medicalItem, emrTemplateId, encounterId);
        newEntry.IsNewMedicalItem = true;
        newEntry.RelatedEncounterId = encounterId;
        newEntry.CreationDate = DateTime.nowMoment().toDate();

        switch (newEntry.EMRTemplateMedicalItemType) {
            case MedicalItemTypeEnum.PastAndOngoingProblemsAndConditions:
                (newEntry as MedicalItemProblemAndCondition).AnteriorityConsultationId = encounterId;
                break;
            case MedicalItemTypeEnum.ScreeningTest:
                let screening = newEntry as MedicalItemScreeningTestResponse;
                if (screening.FollowUps)
                    screening.FollowUps = screening.FollowUps.filter(f => !f.CompletionDate);

                if (cacheStore.ClinicSettings.EnablePreventiveActivityEnhancements) {
                    MedicalItemScreeningTest.autoUpdateStatusAndDueDate(screening);
                }
                break;
        }
        return newEntry;
    }

    private updateScreeningsModelAfterPrintingRequest(request: LocalEvent, store: PatientStore): void {
        let requestId = request.Get<string>("requestId");
        let medicalRequesType = request.Get<MedicalRequestType>("requestType");

        store.updateScreeningsModelAfterPrintingRequest(requestId, medicalRequesType);
    }

    private updateFollowUpComment(items: Immutable.List<MedicalItem>, requestId: string, medicalrequestype: MedicalRequestType, comment: string): Immutable.List<any> {
        if (!items)
            return [].toImmutable();

        let filteredItems = items.toArray();
        for (let x of filteredItems) {
            if (x.EMRTemplateMedicalItemType !== MedicalItemTypeEnum.ScreeningTest)
                continue;

            let screeningtest = x as MedicalItemScreeningTestResponse;
            if (screeningtest.FollowUps) {
                let followup = screeningtest.FollowUps.firstOrDefault(f => f.Id === requestId && f.MedicalRequestType === medicalrequestype);
                if (followup) {
                    followup.CommentsCount = (followup.CommentsCount || 0) + 1;
                    followup.LatestComment = comment;
                }
            }
        }
        return filteredItems.toImmutable();
    }

    private updateFollowUpComposite(items: Immutable.List<FollowUpResponse>, requestId: string, medicalRequestType: MedicalRequestType, comment: string): Immutable.List<FollowUpResponse> {
        if (!items)
            return [].toImmutable();

        let updatedItems = items.toArray();

        updatedItems.mutate(
            f => {
                f.CommentsCount = (f.CommentsCount || 0) + 1;
                f.LatestComment = comment;
            },
            f => f.Id == requestId && f.MedicalRequestType === medicalRequestType
        );

        return updatedItems.toImmutable();
    }

    private togglePharmaNetLoginComponent(request: LocalEvent, store: PatientStore): void {
        const wasShown = store.PatientMedicalItemsModels[request.PatientId].get("ShowPharmaNetComponent");
        store.PatientMedicalItemsModels[request.PatientId] = store.PatientMedicalItemsModels[request.PatientId]
            .set("ShowPharmaNetComponent", !wasShown);
    }

    private toggleAsFavorite(request: LocalEvent, store: PatientStore): void {
        const itemModel = store.PatientMedicalItemsModels[request.PatientId];
        const isFavorite = itemModel.get("IsFavorite");
        store.PatientMedicalItemsModels[request.PatientId] = itemModel
            .set("IsFavorite", !isFavorite);
    }

    private async saveRiskFactors(request: LocalEvent, store: PatientStore): Promise<void> {
        const riskFactors = request.Get<MedicalItemRiskFactor[]>("riskFactors");

        const medicalNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];

        if (medicalNoteModel) {
            const newNotesModel = medicalNoteModel
                .set("MedicalItemsRiskFactor", Immutable.List(riskFactors));
            store.PatientMedicalNoteModels[request.PatientId] = newNotesModel;
        }

        if (medicalSummaryModel) {
            const newSummaryModel = medicalSummaryModel
                .set("MedicalItemsRiskFactor", Immutable.List(riskFactors));
            store.PatientMedicalSummaryModels[request.PatientId] = newSummaryModel;
        }
    }

    private async saveImmunizations(request: LocalEvent, store: PatientStore): Promise<void> {
        const immunizations = request.Get<MedicalItemImmunization[]>("immunizations");

        const medicalNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];

        if (medicalNoteModel) {
            const newNotesModel = medicalNoteModel
                .set("MedicalItemsImmunization", Immutable.List(immunizations));
            store.PatientMedicalNoteModels[request.PatientId] = newNotesModel;
        }

        if (medicalSummaryModel) {
            const newSummaryModel = medicalSummaryModel
                .set("MedicalItemsImmunization", Immutable.List(immunizations));
            store.PatientMedicalSummaryModels[request.PatientId] = newSummaryModel;
        }
    }

    private async toggleFavoriteMedicalItem(request: LocalEvent, store: PatientStore): Promise<void> {
        store.ActionsMedicalItems.toggleFavoriteMedicalItem(
            request.Get<number>("screeningTestDefinitionId"),
            request.Get<string>("screeningTestDefinitionText"),
            request.Get<boolean>("isFavorite"),
            request.Get<Guid>("frequencyDropdownId"),
            request.Get<Guid>("reminderDropdownId"),
            request.Get<number>("startAge"),
            request.Get<number>("endAge")
        );
    }
}

export { PatientMedicalItemsHandler };
